import { error as logError } from '@/lib/logger';

export type ErrorCategory =
  | 'validation'
  | 'network'
  | 'timeout'
  | 'auth'
  | 'permission'
  | 'not_found'
  | 'server'
  | 'upstream'
  | 'rate_limit'
  | 'unknown';

export interface NormalizedError {
  category: ErrorCategory;
  userMessage: string;
  technicalDetail: string;
  statusCode?: number;
  canRetry: boolean;
}

const USER_MESSAGES: Record<ErrorCategory, string> = {
  validation:
    'Não foi possível processar os dados informados. Verifique os campos e tente novamente.',
  network: 'Verifique sua conexão com a internet e tente novamente.',
  timeout:
    'A operação está demorando mais que o esperado. Tente novamente em alguns instantes.',
  auth: 'Sua sessao expirou. Faca login novamente para continuar.',
  permission: 'Voce nao tem permissao para acessar este recurso.',
  not_found: 'O conteudo que voce procura nao foi encontrado.',
  server:
    'Tivemos um problema inesperado. Nossa equipe ja pode verificar o ocorrido.',
  upstream:
    'Servico temporariamente indisponivel. Tente novamente em alguns instantes.',
  rate_limit:
    'Muitas solicitacoes em um curto periodo. Aguarde um momento e tente novamente.',
  unknown: 'Nao foi possivel concluir esta acao. Tente novamente.',
};

export function normalizeError(input: unknown): NormalizedError {
  const technicalDetail = normalizeErrorDetail(input);
  const specificMessage = getSupabaseSpecificMessage(input);
  const category = categorizeError(input);
  const userMessage = specificMessage ?? USER_MESSAGES[category];
  const canRetry =
    category !== 'auth' &&
    category !== 'permission' &&
    category !== 'not_found';

  if (category !== 'unknown' || specificMessage) {
    logError('Error normalized', {
      category,
      technicalDetail,
      userMessage,
      canRetry,
      raw: input,
    });
  }

  return {
    category,
    userMessage,
    technicalDetail,
    canRetry,
  };
}

function getSupabaseSpecificMessage(input: unknown): string | null {
  if (typeof input !== 'object' || input === null) return null;
  const record = input as Record<string, unknown>;
  const code = typeof record.code === 'string' ? record.code : null;
  const message =
    typeof record.message === 'string' ? record.message.toLowerCase() : '';

  if (
    code === 'email_not_confirmed' ||
    message.includes('email not confirmed')
  ) {
    return 'Seu e-mail ainda não foi confirmado. Verifique sua caixa de entrada e spam.';
  }
  if (
    code === 'user_already_exists' ||
    code === 'email_exists' ||
    message.includes('already exists') ||
    message.includes('already registered')
  ) {
    return 'Este e-mail já possui uma conta. Faça login ou recupere sua senha.';
  }
  if (
    code === 'invalid_credentials' ||
    message.includes('invalid login credentials') ||
    message.includes('invalid_credentials')
  ) {
    return 'E-mail ou senha incorretos.';
  }
  if (code === 'user_not_found' || message.includes('user not found')) {
    return 'Usuario nao encontrado. Verifique o e-mail ou solicite acesso.';
  }
  if (
    code === 'weak_password' ||
    message.includes('weak_password') ||
    message.includes('password should be')
  ) {
    return 'A senha nao atende aos requisitos minimos. Use pelo menos 6 caracteres.';
  }
  return null;
}

function normalizeErrorDetail(input: unknown): string {
  if (input instanceof Error) {
    return input.message;
  }
  if (typeof input === 'string') {
    return input;
  }
  if (typeof input === 'object' && input !== null) {
    const record = input as Record<string, unknown>;
    const message = record.message || record.error || record.error_description;
    if (typeof message === 'string') {
      return message;
    }
    return JSON.stringify(record);
  }
  return String(input);
}

function categorizeError(input: unknown): ErrorCategory {
  if (isAuthError(input)) {
    return 'auth';
  }
  if (isPermissionError(input)) {
    return 'permission';
  }
  if (isNotFoundError(input)) {
    return 'not_found';
  }
  if (isRateLimitError(input)) {
    return 'rate_limit';
  }
  if (isValidationError(input)) {
    return 'validation';
  }
  if (isNetworkError(input)) {
    return 'network';
  }
  if (isTimeoutError(input)) {
    return 'timeout';
  }
  if (isUpstreamError(input)) {
    return 'upstream';
  }
  if (isServerError(input)) {
    return 'server';
  }
  return 'unknown';
}

function isValidationError(input: unknown): boolean {
  if (input instanceof Error) {
    const message = input.message.toLowerCase();
    return (
      message.includes('expected string, received') ||
      message.includes('expected number, received') ||
      message.includes('invalid') ||
      message.includes('validation') ||
      message.includes('required') ||
      message.includes('zod')
    );
  }
  if (typeof input === 'object' && input !== null) {
    const record = input as Record<string, unknown>;
    const status = record.status || record.statusCode;
    if (typeof status === 'number' && status >= 400 && status < 500) {
      return true;
    }
  }
  return false;
}

function isNetworkError(input: unknown): boolean {
  if (input instanceof Error) {
    const message = input.message.toLowerCase();
    return (
      message.includes('networkerror') ||
      message.includes('failed to fetch') ||
      message.includes('network request failed') ||
      message.includes('econnreset') ||
      message.includes('enotfound') ||
      message.includes('econnrefused')
    );
  }
  if (typeof input === 'object' && input !== null) {
    const record = input as Record<string, unknown>;
    const status = record.status || record.statusCode;
    if (typeof status === 'number' && status === 0) {
      return true;
    }
  }
  return false;
}

function isTimeoutError(input: unknown): boolean {
  if (input instanceof Error) {
    const message = input.message.toLowerCase();
    return (
      message.includes('timeout') ||
      message.includes('timed out') ||
      message.includes('504')
    );
  }
  if (typeof input === 'object' && input !== null) {
    const record = input as Record<string, unknown>;
    const status = record.status || record.statusCode;
    if (typeof status === 'number' && status === 504) {
      return true;
    }
  }
  return false;
}

function isAuthError(input: unknown): boolean {
  if (typeof input === 'object' && input !== null) {
    const record = input as Record<string, unknown>;
    const status = record.status || record.statusCode;
    const code = record.code;
    if (typeof status === 'number' && status === 401) {
      return true;
    }
    if (
      typeof code === 'string' &&
      /^(invalid_credentials|email_not_confirmed|user_not_found|session_expired|token_expired|token_revoked|missing_session_token|invalid_grant|unsupported_grant_type)/.test(
        code,
      )
    ) {
      return true;
    }
  }
  return false;
}

function isPermissionError(input: unknown): boolean {
  if (typeof input === 'object' && input !== null) {
    const record = input as Record<string, unknown>;
    const status = record.status || record.statusCode;
    if (typeof status === 'number' && status === 403) {
      return true;
    }
  }
  return false;
}

function isNotFoundError(input: unknown): boolean {
  if (typeof input === 'object' && input !== null) {
    const record = input as Record<string, unknown>;
    const status = record.status || record.statusCode;
    if (typeof status === 'number' && status === 404) {
      return true;
    }
  }
  return false;
}

function isRateLimitError(input: unknown): boolean {
  if (typeof input === 'object' && input !== null) {
    const record = input as Record<string, unknown>;
    const status = record.status || record.statusCode;
    if (typeof status === 'number' && status === 429) {
      return true;
    }
  }
  return false;
}

function isUpstreamError(input: unknown): boolean {
  if (typeof input === 'object' && input !== null) {
    const record = input as Record<string, unknown>;
    const status = record.status || record.statusCode;
    if (typeof status === 'number' && (status === 502 || status === 503)) {
      return true;
    }
  }
  return false;
}

function isServerError(input: unknown): boolean {
  if (typeof input === 'object' && input !== null) {
    const record = input as Record<string, unknown>;
    const status = record.status || record.statusCode;
    if (typeof status === 'number' && status >= 500 && status < 600) {
      return true;
    }
  }
  return false;
}
